import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Truck, Save, X, AlertCircle } from "lucide-react";
import Swal from "sweetalert2";

const RegistrarVehiculo = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    placa: "",
    marca: "",
    modelo: "",
    año: "",
    tipo_vehiculo: "CAMION",
    imei: "",
    capacidad_toneladas: "",
    estado: "ACTIVO",
  });
  const [errors, setErrors] = useState({});

  const API_LARAVEL_URL =
    process.env.REACT_APP_API_URL || "http://localhost:8000/api";

  const getApiHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Limpiar error del campo
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      const response = await fetch(`${API_LARAVEL_URL}/flota/vehiculos`, {
        method: "POST",
        headers: getApiHeaders(),
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        Swal.fire({
          icon: "success",
          title: "¡Éxito!",
          text: data.message || "Vehículo registrado correctamente",
        });
        // Limpiar formulario
        setFormData({
          placa: "",
          marca: "",
          modelo: "",
          año: "",
          tipo_vehiculo: "CAMION",
          imei: "",
          capacidad_toneladas: "",
          estado: "ACTIVO",
        });
      } else {
        if (data.errors) {
          setErrors(data.errors);
        } else {
          throw new Error(data.message || "Error al registrar vehículo");
        }
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "No se pudo registrar el vehículo",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      placa: "",
      marca: "",
      modelo: "",
      año: "",
      tipo_vehiculo: "CAMION",
      imei: "",
      capacidad_toneladas: "",
      estado: "ACTIVO",
    });
    setErrors({});
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 mb-4">
        <div className="px-4 py-3">
          <div className="flex items-center">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-2 rounded-lg mr-3">
              <Truck className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                Registrar Vehículo
              </h1>
              <p className="text-sm text-gray-600">
                Agrega un nuevo vehículo a tu flota
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Formulario */}
      <div className="px-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-4xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Placa */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="placa" className="block text-sm font-medium text-gray-700 mb-1">
                  Placa <span className="text-red-500">*</span>
                </label>
                <input
                  id="placa"
                  type="text"
                  name="placa"
                  value={formData.placa}
                  onChange={handleChange}
                  placeholder="Ej: ABC-123"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.placa ? "border-red-500" : "border-gray-300"
                    }`}
                  required
                />
                {errors.placa && (
                  <p className="text-red-500 text-xs mt-1 flex items-center">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    {errors.placa[0]}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="tipo_vehiculo" className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Vehículo <span className="text-red-500">*</span>
                </label>
                <select
                  id="tipo_vehiculo"
                  name="tipo_vehiculo"
                  value={formData.tipo_vehiculo}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="CAMION">Camión</option>
                  <option value="CARRETA">Carreta</option>
                  <option value="VAN">Van</option>
                  <option value="OTROS">Otros</option>
                </select>
              </div>
            </div>

            {/* Marca y Modelo */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="marca" className="block text-sm font-medium text-gray-700 mb-1">
                  Marca
                </label>
                <input
                  id="marca"
                  type="text"
                  name="marca"
                  value={formData.marca}
                  onChange={handleChange}
                  placeholder="Ej: Volvo"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label htmlFor="modelo" className="block text-sm font-medium text-gray-700 mb-1">
                  Modelo
                </label>
                <input
                  id="modelo"
                  type="text"
                  name="modelo"
                  value={formData.modelo}
                  onChange={handleChange}
                  placeholder="Ej: FH16"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Año y Capacidad */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="año" className="block text-sm font-medium text-gray-700 mb-1">
                  Año
                </label>
                <input
                  id="año"
                  type="number"
                  name="año"
                  value={formData.año}
                  onChange={handleChange}
                  placeholder="Ej: 2023"
                  min="1900"
                  max={new Date().getFullYear() + 1}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label htmlFor="capacidad_toneladas" className="block text-sm font-medium text-gray-700 mb-1">
                  Capacidad (Toneladas)
                </label>
                <input
                  id="capacidad_toneladas"
                  type="number"
                  name="capacidad_toneladas"
                  value={formData.capacidad_toneladas}
                  onChange={handleChange}
                  placeholder="Ej: 20.5"
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* IMEI y Estado */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="imei" className="block text-sm font-medium text-gray-700 mb-1">
                  IMEI (GPS)
                </label>
                <input
                  id="imei"
                  type="text"
                  name="imei"
                  value={formData.imei}
                  onChange={handleChange}
                  placeholder="Ej: 355468591479183"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label htmlFor="estado" className="block text-sm font-medium text-gray-700 mb-1">
                  Estado
                </label>
                <select
                  id="estado"
                  name="estado"
                  value={formData.estado}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="ACTIVO">Activo</option>
                  <option value="INACTIVO">Inactivo</option>
                  <option value="MANTENIMIENTO">Mantenimiento</option>
                </select>
              </div>
            </div>

            {/* Botones */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleReset}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors flex items-center"
              >
                <X className="w-4 h-4 mr-2" />
                Limpiar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 text-white bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 rounded-lg font-medium transition-all duration-200 flex items-center shadow-lg hover:shadow-xl disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar Vehículo
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegistrarVehiculo;
